/**
 * Custom Mini Cart Controller
 * - Detects .sticky_nav--stick and toggles .sticky class on #shopify-section-mini-cart
 * - Mobile tap toggle & redirect prevention (stops navigation to /cart)
 * - Dynamic positioning right below visible cart button or sticky header
 * - Smooth re-append on cart update (never disappears)
 * - Safe boundary hover detection (no flickering or false closing on DOM swaps)
 * - Liquid subscription & properties support
 * - Fast single-trip AJAX updates
 */

(function () {
  'use strict';

  if (window.MiniCartInitialized) return;
  window.MiniCartInitialized = true;

  const SECTION_ID = 'mini-cart';
  const SECTION_SELECTOR = '#shopify-section-mini-cart';
  const BUTTON_SELECTOR = '.mini-cart-button';
  const CONTAINER_SELECTOR = '.custom-minicart';
  const STICKY_NAV_STICK_SELECTOR = '.sticky_nav--stick';
  const INTERNAL_HEADER = 'X-Mini-Cart-Request';

  let closeTimeout = null;
  let isUpdating = false;
  let isCursorInside = false;
  let isTouchDevice = false;

  // Track touch devices to disable simulated desktop mouseover/mouseout
  window.addEventListener('touchstart', function () {
    isTouchDevice = true;
  }, { passive: true, once: true });

  function getMiniCartSection() {
    return document.querySelector(SECTION_SELECTOR);
  }

  /* -------------------------------------------------------------
   * Body Scroll & Active Link State Management
   * ----------------------------------------------------------- */

  function unblockScroll() {
    if (document.body.classList.contains('blocked-scroll')) {
      document.body.classList.remove('blocked-scroll');
    }
  }

  function addActiveLink() {
    document.querySelectorAll(BUTTON_SELECTOR).forEach((btn) => {
      btn.classList.add('active_link');
      const parentContainer = btn.closest('.cart-container');
      if (parentContainer) {
        parentContainer.classList.add('active_link');
      }
    });
  }

  function removeActiveLink() {
    document.querySelectorAll('.active_link').forEach((el) => {
      el.classList.remove('active_link');
    });
  }

  function alignMiniCartPosition(triggerBtn = null) {
    const section = getMiniCartSection();
    if (!section) return;

    // 1. If sticky nav is active, set top equal to exact height of .sticky_nav--stick
    const stickyNav = document.querySelector(STICKY_NAV_STICK_SELECTOR);
    if (stickyNav) {
      const stickyHeight = Math.round(stickyNav.offsetHeight || stickyNav.getBoundingClientRect().height);
      if (stickyHeight > 0) {
        section.style.top = stickyHeight + 'px';
        return;
      }
    }

    // 2. If a specific button triggered the open and is visible, position below it
    let btn = triggerBtn && triggerBtn.offsetParent !== null ? triggerBtn : null;

    // 3. Otherwise find the first visible mini-cart button (mobile or desktop)
    if (!btn) {
      const buttons = document.querySelectorAll(BUTTON_SELECTOR);
      for (let i = 0; i < buttons.length; i++) {
        if (buttons[i].offsetParent !== null) {
          btn = buttons[i];
          break;
        }
      }
    }

    if (btn) {
      const rect = btn.getBoundingClientRect();
      if (rect.bottom > 0) {
        section.style.top = Math.round(rect.bottom) + 'px';
        return;
      }
    }

    // Default fallback
    section.style.top = '';
  }

  /* -------------------------------------------------------------
   * Sticky Nav Detection (.sticky_nav--stick -> .sticky class)
   * ----------------------------------------------------------- */

  function updateStickyStatus() {
    const section = getMiniCartSection();
    if (!section) return;

    // Condition to check if .sticky_nav--stick is present in DOM
    const stickyNav = document.querySelector(STICKY_NAV_STICK_SELECTOR);
    const isStickyNavActive = Boolean(stickyNav);

    if (isStickyNavActive) {
      if (!section.classList.contains('sticky')) {
        section.classList.add('sticky');
      }
      // Sticky hone par jitni height .sticky_nav--stick ki hai utna top se px do
      const stickyHeight = Math.round(stickyNav.offsetHeight || stickyNav.getBoundingClientRect().height);
      if (stickyHeight > 0) {
        section.style.top = stickyHeight + 'px';
      }
    } else {
      if (section.classList.contains('sticky')) {
        section.classList.remove('sticky');
      }
      // Unstick hone par normal header ke mutabiq top set karo
      alignMiniCartPosition();
    }
  }

  function initStickyNavObserver() {
    // Initial check on load
    updateStickyStatus();
    unblockScroll();

    // Listen to scroll events with requestAnimationFrame
    let ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateStickyStatus();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // MutationObserver to immediately catch class changes by Headhesive / theme scripts
    if (window.MutationObserver) {
      const observer = new MutationObserver(function (mutations) {
        for (let i = 0; i < mutations.length; i++) {
          const m = mutations[i];
          if (m.type === 'attributes' && m.attributeName === 'class') {
            updateStickyStatus();
            // Automatically clear theme's stuck scroll if added
            unblockScroll();
            break;
          }
        }
      });

      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true,
      });
    }
  }

  /* -------------------------------------------------------------
   * Visibility Controls
   * ----------------------------------------------------------- */

  let lastToggleTime = 0;

  function openMiniCart(triggerBtn = null) {
    clearTimeout(closeTimeout);
    const section = getMiniCartSection();
    if (section) {
      updateStickyStatus();
      alignMiniCartPosition(triggerBtn);
      section.classList.add('active');
      addActiveLink();
      unblockScroll();
    }
  }

  function closeMiniCart(immediate = false) {
    clearTimeout(closeTimeout);

    // If an update is currently in flight, do not close yet
    if (isUpdating) {
      return;
    }

    const doClose = () => {
      const section = getMiniCartSection();
      if (section && !isUpdating) {
        section.classList.remove('active');
      }
      removeActiveLink();
      unblockScroll();
      document.body.classList.remove('is-active');
    };

    if (immediate) {
      doClose();
      return;
    }

    closeTimeout = setTimeout(() => {
      if (!isCursorInside) {
        doClose();
      }
    }, 280);
  }

  function cancelClose() {
    clearTimeout(closeTimeout);
  }

  function toggleMiniCart(e, triggerBtn = null) {
    const now = Date.now();
    // Debounce to prevent synthetic click after touchend double-firing
    if (now - lastToggleTime < 350) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }
    lastToggleTime = now;

    if (e) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }
    }

    const section = getMiniCartSection();
    if (!section) return;

    if (section.classList.contains('active')) {
      isCursorInside = false;
      closeMiniCart(true);
    } else {
      openMiniCart(triggerBtn);
    }
  }

  /* -------------------------------------------------------------
   * Cart Count Badges (Header ONLY)
   * ----------------------------------------------------------- */

  function updateCartCounts(count) {
    if (typeof count === 'undefined' || count === null) return;
    
    const badgeElements = document.querySelectorAll(
      '.mini-cart-button .cart_count, .mini-cart-button span, .top-bar .cart_count, header .cart_count, .header .cart_count'
    );

    badgeElements.forEach((el) => {
      if (!el.closest(SECTION_SELECTOR)) {
        el.textContent = count;
      }
    });
  }

  async function syncCartCountFromApi() {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js?timestamp=${Date.now()}`);
      if (res.ok) {
        const cart = await res.json();
        updateCartCounts(cart.item_count);
      }
    } catch (e) {
      console.warn('Could not sync cart count:', e);
    }
  }

  /* -------------------------------------------------------------
   * Smooth Section Re-append
   * ----------------------------------------------------------- */

  function renderSectionHtml(rawHtml, forceOpen = true) {
    const section = getMiniCartSection();
    if (!section || !rawHtml) return;

    cancelClose();
    if (forceOpen) {
      updateStickyStatus();
      alignMiniCartPosition();
      section.classList.add('active');
      addActiveLink();
      unblockScroll();
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, 'text/html');

    const newContainer = doc.querySelector(CONTAINER_SELECTOR);
    const currentContainer = section.querySelector(CONTAINER_SELECTOR);

    if (currentContainer && newContainer) {
      // Smooth in-place replacement of container only
      currentContainer.replaceWith(newContainer);
    } else {
      // Fallback: replace innerHTML of section
      const returnedSection = doc.querySelector(SECTION_SELECTOR);
      section.innerHTML = returnedSection ? returnedSection.innerHTML : rawHtml;
    }

    // Keep active class solid
    if (forceOpen) {
      section.classList.add('active');
      addActiveLink();
      unblockScroll();
    }

    document.dispatchEvent(new CustomEvent('mini-cart:rendered'));
  }

  /* -------------------------------------------------------------
   * Fetch Entire Section
   * ----------------------------------------------------------- */

  async function fetchMiniCartSection(shouldOpen = false) {
    const section = getMiniCartSection();
    if (!section) return;

    try {
      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}cart?section_id=${SECTION_ID}&timestamp=${Date.now()}`
      );
      if (!response.ok) throw new Error('Failed to fetch mini cart section');
      const html = await response.text();

      renderSectionHtml(html, shouldOpen || section.classList.contains('active'));
      syncCartCountFromApi();
    } catch (err) {
      console.error('Error fetching mini-cart section:', err);
    }
  }

  /* -------------------------------------------------------------
   * Item Quantity Change / Remove (Smooth & Stable)
   * ----------------------------------------------------------- */

  async function changeItemQuantity(itemKey, newQuantity, triggerElement = null) {
    if (isUpdating) return;
    isUpdating = true;
    cancelClose();

    const section = getMiniCartSection();
    if (section) {
      section.classList.add('active');
    }

    // Find the specific row being updated
    let targetRow = null;
    if (triggerElement) {
      targetRow = triggerElement.closest('.custom-cart__row');
    }
    if (!targetRow && itemKey) {
      targetRow = document.querySelector(`.custom-cart__row[data-key="${itemKey}"]`);
    }

    // Show spinner ONLY on this specific item row
    if (targetRow) {
      targetRow.classList.add('custom-cart__row--loading');
      if (!targetRow.querySelector('.custom-cart__row-loader')) {
        const loader = document.createElement('div');
        loader.className = 'custom-cart__row-loader';
        loader.innerHTML = '<div class="custom-cart__spinner"></div>';
        targetRow.appendChild(loader);
      }
    }

    try {
      // 1. Single-round-trip request requesting rendered section in one go (2x faster)
      const changeResponse = await fetch(
        `${window.Shopify?.routes?.root || '/'}cart/change?sections=${SECTION_ID}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            [INTERNAL_HEADER]: 'true',
          },
          body: JSON.stringify({
            id: itemKey,
            quantity: Math.max(0, parseInt(newQuantity, 10) || 0),
            sections: SECTION_ID,
          }),
        }
      );

      const cartData = await changeResponse.json();

      // 2. Update cart counts in header
      if (cartData && typeof cartData.item_count !== 'undefined') {
        updateCartCounts(cartData.item_count);
      }

      // 3. If Shopify returned the rendered section directly (single trip)
      if (cartData && cartData.sections && cartData.sections[SECTION_ID]) {
        renderSectionHtml(cartData.sections[SECTION_ID], true);
      } else {
        // Fallback only if sections wasn't in response
        const sectionResponse = await fetch(
          `${window.Shopify?.routes?.root || '/'}cart?section_id=${SECTION_ID}&timestamp=${Date.now()}`
        );
        const sectionHtml = await sectionResponse.text();
        renderSectionHtml(sectionHtml, true);
      }

      document.dispatchEvent(new CustomEvent('mini-cart:updated', { detail: { cart: cartData } }));
    } catch (error) {
      console.error('Error changing mini cart quantity:', error);
      await fetchMiniCartSection(true);
    } finally {
      if (targetRow) {
        targetRow.classList.remove('custom-cart__row--loading');
      }

      // Keep isUpdating protected for a brief moment to swallow synthetic mouse events
      setTimeout(() => {
        isUpdating = false;
      }, 300);
    }
  }

  /* -------------------------------------------------------------
   * Event Delegation (Clicks & Inputs)
   * ----------------------------------------------------------- */

  function initSectionDelegation() {
    // Intercept clicks on .mini-cart-button in capture phase to stop ANY link navigation
    function handleCartButtonClick(e) {
      const cartBtn = e.target.closest(BUTTON_SELECTOR);
      if (cartBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }
        toggleMiniCart(e);
        return false;
      }
    }

    // Capture phase click & touchend prevents default link jump immediately
    document.addEventListener('click', handleCartButtonClick, true);
    document.addEventListener('touchend', function (e) {
      const cartBtn = e.target.closest(BUTTON_SELECTOR);
      if (cartBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }
        toggleMiniCart(e, cartBtn);
        return;
      }

      const section = getMiniCartSection();
      if (!section || !section.classList.contains('active')) return;

      // If continue shopping is tapped on mobile
      if (e.target.closest('.cart-continue-shopping, [data-action="close"]')) {
        e.preventDefault();
        isCursorInside = false;
        closeMiniCart(true);
        return;
      }

      // If tap is outside mini-cart and outside buttons
      if (!section.contains(e.target)) {
        isCursorInside = false;
        closeMiniCart(true);
      }
    }, { passive: false });

    // Inside mini cart clicks
    document.addEventListener('click', function (e) {
      const section = getMiniCartSection();

      // Continue Shopping button click
      if (e.target.closest('.cart-continue-shopping, [data-action="close"]')) {
        e.preventDefault();
        isCursorInside = false;
        closeMiniCart(true);
        return;
      }

      if (section && section.contains(e.target)) {
        cancelClose();

        // Remove button
        const removeBtn = e.target.closest('[data-action="remove"], .custom-cart__remove');
        if (removeBtn) {
          e.preventDefault();
          const itemEl = removeBtn.closest('.custom-cart__row');
          const itemKey = removeBtn.getAttribute('data-key') || itemEl?.getAttribute('data-key');
          if (itemKey) {
            changeItemQuantity(itemKey, 0, itemEl);
          }
          return;
        }

        // Minus button
        const minusBtn = e.target.closest('[data-action="decrease"], .custom-cart__qty-dec');
        if (minusBtn) {
          e.preventDefault();
          const itemEl = minusBtn.closest('.custom-cart__row');
          const input = itemEl?.querySelector('.custom-cart__qty-val');
          const itemKey = minusBtn.getAttribute('data-key') || itemEl?.getAttribute('data-key');
          if (input && itemKey) {
            const currentQty = parseInt(input.value, 10) || 1;
            changeItemQuantity(itemKey, currentQty - 1, itemEl);
          }
          return;
        }

        // Plus button
        const plusBtn = e.target.closest('[data-action="increase"], .custom-cart__qty-inc');
        if (plusBtn) {
          e.preventDefault();
          const itemEl = plusBtn.closest('.custom-cart__row');
          const input = itemEl?.querySelector('.custom-cart__qty-val');
          const itemKey = plusBtn.getAttribute('data-key') || itemEl?.getAttribute('data-key');
          if (input && itemKey) {
            const currentQty = parseInt(input.value, 10) || 0;
            changeItemQuantity(itemKey, currentQty + 1, itemEl);
          }
          return;
        }

        return;
      }

      // Outside click on mobile or desktop -> close if clicked outside
      if (!e.target.closest(BUTTON_SELECTOR) && section && section.classList.contains('active')) {
        isCursorInside = false;
        closeMiniCart(true);
      }
    });

    // Close on outside focus (e.g. search bar, input fields, links outside mini cart)
    document.addEventListener('focusin', function (e) {
      const section = getMiniCartSection();
      if (!section || !section.classList.contains('active')) return;

      if (!section.contains(e.target) && !e.target.closest(BUTTON_SELECTOR)) {
        isCursorInside = false;
        closeMiniCart(true);
      }
    });

    // Manual quantity input change
    document.addEventListener('change', function (e) {
      if (e.target.matches('.custom-cart__qty-val')) {
        cancelClose();
        const input = e.target;
        const itemEl = input.closest('.custom-cart__row');
        const itemKey = input.getAttribute('data-key') || itemEl?.getAttribute('data-key');
        if (itemKey) {
          const qty = parseInt(input.value, 10);
          changeItemQuantity(itemKey, isNaN(qty) ? 0 : qty, itemEl);
        }
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        isCursorInside = false;
        closeMiniCart(true);
      }
    });

    // Reposition on window resize / orientation change
    window.addEventListener('resize', function () {
      const section = getMiniCartSection();
      if (section && section.classList.contains('active')) {
        alignMiniCartPosition();
      }
    }, { passive: true });
  }

  /* -------------------------------------------------------------
   * Boundary Hover Listeners (Desktop ONLY)
   * ----------------------------------------------------------- */

  function setupHoverListeners() {
    // Continuous mousemove check for reliable cursor tracking on desktop
    document.addEventListener('mousemove', function (e) {
      if (isTouchDevice) return;
      const section = getMiniCartSection();
      const inBtn = !!e.target.closest(BUTTON_SELECTOR);
      const inSection = !!(section && section.contains(e.target));

      if (inBtn || inSection) {
        isCursorInside = true;
        cancelClose();
      }
    });

    // Mouse over header buttons
    document.addEventListener('mouseover', function (e) {
      if (isTouchDevice) return;
      const btn = e.target.closest(BUTTON_SELECTOR);
      const section = getMiniCartSection();
      const inSection = !!(section && section.contains(e.target));

      if (btn || inSection) {
        isCursorInside = true;
        cancelClose();
        openMiniCart(btn);
      }
    });

    // Mouse out check
    document.addEventListener('mouseout', function (e) {
      if (isTouchDevice) return;
      const section = getMiniCartSection();
      const toElement = e.relatedTarget;

      if (toElement) {
        const stillInBtn = !!toElement.closest(BUTTON_SELECTOR);
        const stillInSection = !!(section && section.contains(toElement));
        if (stillInBtn || stillInSection) {
          isCursorInside = true;
          cancelClose();
          return;
        }
      }

      isCursorInside = false;
      closeMiniCart(false);
    });
  }

  /* -------------------------------------------------------------
   * Global Cart Interceptors (Catch Add-to-Cart anywhere)
   * ----------------------------------------------------------- */

  function setupGlobalInterceptors() {
    // 1. Intercept window.fetch
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
      const [resource, config] = args;
      const url = typeof resource === 'string' ? resource : resource?.url || '';

      const isInternal = config?.headers && (
        (config.headers instanceof Headers && config.headers.get(INTERNAL_HEADER)) ||
        config.headers[INTERNAL_HEADER]
      );

      const response = await originalFetch.apply(this, args);

      try {
        if (!isInternal && response.ok) {
          const isAdd = url.includes('/cart/add');
          const isChange = url.includes('/cart/change') || url.includes('/cart/clear') || url.includes('/cart/update');

          if (isAdd) {
            fetchMiniCartSection(true);
            setTimeout(unblockScroll, 600);
          } else if (isChange) {
            fetchMiniCartSection(false);
          }
        }
      } catch (err) {
        console.error('MiniCart fetch interceptor error:', err);
      }

      return response;
    };

    // 2. Intercept XMLHttpRequest
    const originalXhrOpen = XMLHttpRequest.prototype.open;
    const originalXhrSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this._miniCartUrl = url;
      return originalXhrOpen.apply(this, [method, url, ...rest]);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      this.addEventListener('load', function () {
        if (this._miniCartUrl && this.status >= 200 && this.status < 300) {
          const url = String(this._miniCartUrl);
          const isAdd = url.includes('/cart/add');
          const isChange = url.includes('/cart/change') || url.includes('/cart/clear') || url.includes('/cart/update');

          if (isAdd) {
            fetchMiniCartSection(true);
            setTimeout(unblockScroll, 600);
          } else if (isChange) {
            fetchMiniCartSection(false);
          }
        }
      });
      return originalXhrSend.apply(this, args);
    };

    // 3. Custom events
    ['cart:updated', 'cart:refresh', 'theme:cart:change', 'ajaxCart.afterCartLoad'].forEach((eventName) => {
      document.addEventListener(eventName, function () {
        fetchMiniCartSection(false);
      });
    });
  }

  /* -------------------------------------------------------------
   * Initialization
   * ----------------------------------------------------------- */

  function init() {
    initStickyNavObserver();
    setupHoverListeners();
    initSectionDelegation();
    setupGlobalInterceptors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.MiniCart = {
    open: openMiniCart,
    close: () => closeMiniCart(true),
    toggle: toggleMiniCart,
    update: fetchMiniCartSection,
    changeQuantity: changeItemQuantity,
    updateStickyStatus: updateStickyStatus
  };
})();
